import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HygieneFiltersModel } from 'src/app/models/filters/implementations/pageFilters/hygieneFilters.model';
import { InventoryFiltersModel } from 'src/app/models/filters/implementations/pageFilters/inventoryFilters.model';
import { PlaybookExecutionsFilters } from 'src/app/models/filters/implementations/pageFilters/playbookExecutionsFilters.model';
import { PlaybooksFiltersModel } from 'src/app/models/filters/implementations/pageFilters/playbooksFilters.model';
import { ViolationsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/violationsFilters.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { PlaybooksHttpServiceService } from '../httpServices/playbooksHttpService/playbooks-http-service.service';
import { InventoryHttpService } from '../httpServices/inventoryHttpService/inventory-http.service';
import { StoreService } from '../infra/store/store.service';
import { FiltersQueryTransformer } from './filtersQueryTransformer.ts/filtersQueryTransformer';
import { DashboardsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/dashboardsFilters.model';
import { LoggerService } from '../infra/logger/logger.service';
import { ViolationRulesHttpService } from '../httpServices/governance/violationRulesHttpService/violation-rules-http.service';
import { FeatureFlagsService } from '../infra/featureFlags/feature-flags.service';
import { AiActivityFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiActivityFilters.model';
import { AiAgentStepsHttpService } from '../httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AiFindingsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model';
import { AiFindingsHttpService } from '../httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { EnterpriseAiDetectionTabsEnum } from 'src/app/views/enterprise-ai/enterprise-ai-detection-tabs-management/enterprise-ai-detection-tabs-enum';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { AiDataInsightsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiDataInsightsFilters.model';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { ViolationRulesFiltersModel } from 'src/app/components/violation-rules/filters/violation-rules-filters.model';

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  private readonly _violationsFilter: ViolationsFiltersModel;
  private readonly _inventoryFilters: InventoryFiltersModel;
  private readonly _hygieneFilter: HygieneFiltersModel;
  private readonly _playbooksFilter: PlaybooksFiltersModel;
  private readonly _playbookExecutionsFilter: PlaybookExecutionsFilters;
  private readonly _dashboardsFilter: DashboardsFiltersModel;
  private readonly _aiActivityFilter: AiActivityFiltersModel;
  private readonly _aiFindingsFilter: AiFindingsFiltersModel;
  private readonly _aiDataInsightsFilter: AiDataInsightsFiltersModel;
  private readonly _violationRulesFilter: ViolationRulesFiltersModel;

  private _lastFiltersFromQuery = '';
  private _lastTab = '';

  constructor(
    public featureFlagsService: FeatureFlagsService,
    private readonly _router: Router,
    _store: StoreService,
    _activatedRoute: ActivatedRoute,
    _logger: LoggerService,
    _violationRulesHttpService: ViolationRulesHttpService,
    _inventoryHttpService: InventoryHttpService,
    _playbooksHttpService: PlaybooksHttpServiceService,
    _aiActivityHttpService: AiAgentStepsHttpService,
    _aiFindingsHttpService: AiFindingsHttpService,
    _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    _beautifyDisplayStringPipe: BeautifyDisplayStringPipe
  ) {
    this._violationsFilter = new ViolationsFiltersModel(
      _router,
      _store,
      _violationRulesHttpService,
      _inventoryHttpService,
      featureFlagsService
    );
    this._inventoryFilters = new InventoryFiltersModel(
      _router,
      _store,
      _inventoryHttpService,
      featureFlagsService,
      _beautifyDisplayStringPipe
    );
    this._hygieneFilter = new HygieneFiltersModel(
      _router,
      _store,
      _violationRulesHttpService,
      _inventoryHttpService,
      featureFlagsService
    );
    this._playbooksFilter = new PlaybooksFiltersModel(_router, _store);
    this._playbookExecutionsFilter = new PlaybookExecutionsFilters(_router, _store, _playbooksHttpService);
    this._dashboardsFilter = new DashboardsFiltersModel(_router, _store, _inventoryHttpService);
    this._aiActivityFilter = new AiActivityFiltersModel(
      _router,
      _store,
      _aiActivityHttpService,
      featureFlagsService,
      _displayCamelCaseStringPipe
    );
    this._aiFindingsFilter = new AiFindingsFiltersModel(_router, _store, _aiActivityHttpService, featureFlagsService);
    this._aiDataInsightsFilter = new AiDataInsightsFiltersModel(_router, _store);
    this._violationRulesFilter = new ViolationRulesFiltersModel(_router);
  }

  public getFilters(filterType: PageWithFiltersEnum) {
    switch (filterType) {
      case PageWithFiltersEnum.Violations:
        return this._violationsFilter;

      case PageWithFiltersEnum.Inventory:
        return this._inventoryFilters;

      case PageWithFiltersEnum.Hygiene:
        return this._hygieneFilter;

      case PageWithFiltersEnum.Playbooks:
        return this._playbooksFilter;

      case PageWithFiltersEnum.Executions:
        return this._playbookExecutionsFilter;

      case PageWithFiltersEnum.Dashboards:
        return this._dashboardsFilter;

      case PageWithFiltersEnum.AiActivity:
        return this._aiActivityFilter;

      case PageWithFiltersEnum.AiFindings:
        return this._aiFindingsFilter;

      case PageWithFiltersEnum.AiDataInsights:
        return this._aiDataInsightsFilter;

      case PageWithFiltersEnum.Policies:
        return this._violationRulesFilter;

      default:
        throw new Error(`FiltersService getFilters: Missing support for ${filterType}`);
    }
  }

  public updateFiltersFromQuery(filters?: string, tab?: string) {
    if (filters === undefined || filters == '') {
      if (filters !== this._lastFiltersFromQuery) {
        if (this._router.url.includes('violations')) {
          this._violationsFilter.updateFiltersByKeyValue({});
        } else if (this._router.url.includes('inventory')) {
          this._inventoryFilters.updateFiltersByKeyValue({});
        } else if (this._router.url.includes('hygiene')) {
          this._hygieneFilter.updateFiltersByKeyValue({});
        } else if (this._router.url.includes('playbooks')) {
          if (tab === 'executionHistory') {
            this._playbookExecutionsFilter.updateFiltersByKeyValue({});
          }
          if (tab === 'yourPlaybooks') {
            this._playbooksFilter.updateFiltersByKeyValue({});
          }
        } else if (this._router.url.includes('ai-detection')) {
          this._lastFiltersFromQuery = '';
          this._lastTab = '';
          if (tab === EnterpriseAiDetectionTabsEnum.Activity) {
            this._aiActivityFilter.updateFiltersByKeyValue({});
          } else if (tab === EnterpriseAiDetectionTabsEnum.Findings) {
            this._aiFindingsFilter.updateFiltersByKeyValue({});
          } else if (tab === EnterpriseAiDetectionTabsEnum.DataInsights) {
            this._aiDataInsightsFilter.updateFiltersByKeyValue({});
          } else if (tab === undefined) {
            this._aiActivityFilter.updateFiltersByKeyValue({});
            this._aiFindingsFilter.updateFiltersByKeyValue({});
          }
        }
      }
      return;
    }

    const filtersAfterTrim = filters.trim();

    if (filtersAfterTrim === this._lastFiltersFromQuery && tab === this._lastTab) {
      return;
    }
    const queryWithOnlyValidAsDictionary = FiltersQueryTransformer.transformFromQuery(filters);
    this._lastFiltersFromQuery = filtersAfterTrim;
    this._lastTab = tab ? tab : '';

    if (Object.keys(queryWithOnlyValidAsDictionary).length > 0) {
      if (this._router.url.includes('violations')) {
        this._violationsFilter.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
      } else if (this._router.url.includes('inventory')) {
        this._inventoryFilters.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
      } else if (this._router.url.includes('hygiene')) {
        this._hygieneFilter.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
      } else if (this._router.url.includes('playbooks')) {
        if (tab === 'executionHistory') {
          this._playbookExecutionsFilter.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
        }
        if (tab === 'yourPlaybooks') {
          this._playbooksFilter.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
        }
      } else if (this._router.url.includes('ai-detection')) {
        if (tab === EnterpriseAiDetectionTabsEnum.Activity) {
          this._aiActivityFilter.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
        } else if (tab === EnterpriseAiDetectionTabsEnum.Findings) {
          this._aiFindingsFilter.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
        } else if (tab === EnterpriseAiDetectionTabsEnum.DataInsights) {
          this._aiDataInsightsFilter.updateFiltersByKeyValue(queryWithOnlyValidAsDictionary);
        }
      }
    }
  }
}
